<template>
  <div
    class="bg-white w-full rounded-lg  transition-all duration-200 cursor-pointer relative"
    @click="$emit('click')"
  >
      <!-- Header with Status -->
      <div class="flex w-full text-xs font-medium bg-surface-level-1 ">
        <div
          class=" w-full px-2 py-1"

        >
          <i :class="statusIcon" class="mr-0.25"></i>
          {{ eventStatusText }}
        </div>
      </div>

      <!-- Event Info -->
      <div class="flex gap-4 justify-between w-full">
        <div class="flex-grow min-w-0 overflow-hidden">
          <EventListItem :poker-run-event="pokerRunEvent" @click="$emit('click')" />
        </div>

        <div class="flex-shrink-0 mx-1 px-1 relative">
          <AppOptionMenu @click="handleMenuAction" :menuItems="menuItems" />
        </div>
      </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";
import { useRouter } from 'vue-router';
import Constants from "@/models/Constants";
import { Stop } from "@/models/Stop";
import { PokerRunEvent } from "@/models/PokerRunEvent";
import useDate from "@/modules/useDate";
import useEvent from "@/modules/useEvent";
import AppOptionMenu from "./AppOptionMenu.vue";
import EventListItem from "./EventListItem.vue";
import PokerRunEventDataService from "@/services/PokerRunEventDataService";
import { useUserProfileStore } from "@/store/UserProfile";
import useSharing from "@/modules/useSharing";



const { profile } = useUserProfileStore();
const { getDateTime, getDate, formatFullDatetime } = useDate();
const router = useRouter();

interface Props {
  pokerRunEvent: PokerRunEvent;
}

const props = defineProps<Props>();


const { shareEvent, copyEventLinkToClipboard, shareEventOnFacebook } = useSharing();
const {  isPastEvent } = useEvent();
const emit = defineEmits(['click']);

const isFreeEvent = props.pokerRunEvent?.lineItems?.every(item => item.amount === 0) &&
                     props.pokerRunEvent?._internal?.serviceFee === 0;

const eventStatus = computed(() => {
  const status = props.pokerRunEvent?.status;
  const totalCards = props.pokerRunEvent?.stops?.reduce((acc:number, stop:Stop) => acc + stop.cardsPerStop, 0);
  //if allowInAppPurchase is true, then check if payouts are configured otherwise true
  const payoutsConfigured = !props.pokerRunEvent?.eventSettings?.allowInAppPurchase || props.pokerRunEvent?._internal?.connectedAccountDetails?.onboarded || isFreeEvent;

  if(isPastEvent(props.pokerRunEvent)) {

    // How to determine if payouts happened?
    if(!payoutsConfigured) return 'pendingPaymentMethod';
    if(status === 'active') return 'pendingCloseout';
  } else {
    if (status === 'active' &&  totalCards < 5) return 'activeCardsNotAssigned';
    if (status === 'active' && !payoutsConfigured) return 'pendingPaymentMethod';
    if (status === 'active' ) return 'active';
  }
  if (status === 'completed') return 'completed';
  return 'setup';
});

const eventStatusText = computed(() => {
    const statusText = {
      'active': 'Published',
      'activeCardsNotAssigned': 'Published - stop setup incomplete',
      'pendingPaymentMethod': 'Published - payout method not configured',
      'pendingPayouts': 'Pending payouts',
      'pendingCloseout': 'Pending closeout',
      'setup': 'Draft',
      'completed': 'Complete',
      'inprogress': 'In progress',
    };

    return statusText[eventStatus.value] || statusText['setup'];
  });


  const statusIcon = computed(() => {
    const iconMap = {
      'active': 'fas fa-calendar text-confirm',
      'activeCardsNotAssigned': 'fas fa-circle-info text-caution',
      'pendingPaymentMethod': 'fas fa-circle-info text-caution',
      'pendingPayouts': 'fas fa-dollar-sign text-informative',
      'pendingCloseout': 'fas fa-clock text-informative',
      'setup': 'fas fa-pencil text-informative',
      'completed': 'fas fa-check text-confirm',
      'inprogress': 'In progress text-confirm',
    };

    return iconMap[eventStatus.value] || iconMap['setup'];
  });

const eventDate = computed(() => getDate(props.pokerRunEvent?.eventDate));
const startTime = computed(() => {
  if (props.pokerRunEvent?.stops && props.pokerRunEvent?.stops[0]?.expectedArrivalTime) {
    return new Date(props.pokerRunEvent.stops[0].expectedArrivalTime);
  }
  return getDateTime(props.pokerRunEvent?.eventDate);
});

const formatEventDate = computed(() => {
  if (!startTime.value) return '';
  return formatFullDatetime(startTime.value);
});

const registrationFee = computed(() => {
  const item = props.pokerRunEvent?.lineItems?.find(
    (i) => i.itemType === Constants.FEE_TYPE
  );
  return item || null;
});

const formattedFee = computed(() => {
  if (!registrationFee.value?.amount) return '';
  return registrationFee.value.amount.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
  });
});

const showEventImage = (e: Event) => {
  // Implement image preview logic here
  e.stopPropagation();
};

const menuItems = computed(() =>
  props.pokerRunEvent.organizerId === profile.id ? [
    {
      label: 'Duplicate event',
      action: 'duplicate'
    },
    {
      label: 'Share event',
      action: 'share'
    },
    {
      label: 'Copy event link',
      action: 'copyLink'
    },
    {
      label: 'Leaderboard',
      action: 'leaderboard'
    },
    {
      label: 'Participants',
      action: 'participants'
    }
  ] : [
    {
      label: 'Share event',
      action: 'share'
    },
    {
      label: 'Copy event link',
      action: 'copyLink'
    },
    {
      label: 'Leaderboard',
      action: 'leaderboard'
    },
    {
      label: 'Participants',
      action: 'participants'
    }
  ]
);

const handleMenuAction = (action: string) => {

  switch (action) {
    case 'duplicate':
      PokerRunEventDataService.duplicateEvent(props.pokerRunEvent.id, profile.id)
      .then((newEventId) => {
        if(newEventId === null) return;
        router.push({
          name: 'ManageEventSummary',
          params: { id: newEventId }
        });
      });
      break;
    case 'edit':
      router.push({
        name: 'ManageEventInfo',
        params: { id: props.pokerRunEvent.id }
      });
      break;
    case 'share':
      shareEvent(props.pokerRunEvent);
      break;
    case 'copyLink':
      copyEventLinkToClipboard(props.pokerRunEvent);
      break;
    case 'leaderboard':
      router.push({
        name: 'Leaderboard',
        params: { id: props.pokerRunEvent.id }
      });
      break;
    case 'participants':
    router.push({
        name: 'Participants',
        params: { id: props.pokerRunEvent.id }
      });
      break;
  }
};


</script>



